// session.js
// Central handling for an expired/rejected auth token. Before this the webapp had
// no expiry handling at all: userData was read from localStorage once at load and
// a 401 from any service was simply swallowed, so an iPad left running would keep
// showing a logged-in screen while every request quietly failed.
import { forceLogout, isSessionValid } from "./auth";

// How often the watchdog re-checks validTill
const EXPIRY_CHECK_INTERVAL_MS = 60 * 1000;

// Requests that are expected to run while logged out - never treat their
// failures as an expired session.
const AUTH_FREE_PATHS = [
  "operatorLogin",
  "forgotpassword",
  "resetpassword",
  "logout"
];

const isAuthFreeRequest = (url) => {
  if (!url) return false;
  const target = String(url).toLowerCase();
  return AUTH_FREE_PATHS.some((path) => target.includes(path.toLowerCase()));
};

// Only end the session for a 401 that happened on a call we made as a logged-in
// operator. Anything else (login failures, network errors) is left alone.
export const handleUnauthorized = (url) => {
  if (isAuthFreeRequest(url)) return;
  if (localStorage.getItem("isLogged") !== "true") return;
  forceLogout(`401 from ${url || "server"}`);
};

let installed = false;

// Installed once from index.js. Covers the three ways this app talks to the
// backend: axios (default instance), window.fetch, and the validTill watchdog.
export const installSessionGuards = (axios) => {
  if (installed) return;
  installed = true;

  // 1. axios - every call except LoginForm's own instance goes through the default one
  if (axios) {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error && error.response && error.response.status === 401) {
          handleUnauthorized(error.config && error.config.url);
        }
        return Promise.reject(error);
      }
    );
  }

  // 2. fetch - patched centrally rather than at the ~15 call sites that use it
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (response.status === 401) {
      const [input] = args;
      const url = typeof input === "string" ? input : input && input.url;
      handleUnauthorized(url);
    }
    return response;
  };

  // 3. validTill watchdog - catches expiry even when the iPad is idle and no
  // request has been made to surface a 401
  setInterval(() => {
    if (localStorage.getItem("isLogged") !== "true") return; // not logged in, nothing to expire
    if (!isSessionValid()) {
      forceLogout("token validTill elapsed");
    }
  }, EXPIRY_CHECK_INTERVAL_MS);
};
